import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { MyOrder, OrderRow } from 'ts/shared/interfaces/MOResponse';
import { getStatus } from 'constants/mo';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { CancelOrderParams, cancelOrder } from 'services/myOrders';
import useModalClass from 'hooks/useModalClass';
import useRecaptcha from 'hooks/useRecaptcha';
import { showError, showSuccess } from 'utils/utils';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoCancelModalProps {
  orderRow: OrderRow;
  onSuccess: (res: MyOrder) => void;
  trashIcon?: boolean;
}

const MoCancelModal: FC<MoCancelModalProps> = ({ orderRow, onSuccess, trashIcon = false }) => {
  const [t] = useTranslation();
  const { activeOrder } = useContext(MoContext);
  const isActive =
    activeOrder?.fullSegment.orderSegId === orderRow.segmentId ||
    activeOrder?.orderRow.segmentId === orderRow.segmentId;
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [step, setStep] = useState(0);
  const { handleRecapcha } = useRecaptcha();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (openModal) {
      setStep(0);
    }
  }, [openModal]);

  const onClickCancel = async () => {
    setOpenModal(false);
    const token = await handleRecapcha();

    setIsLoading(true);

    if (!token) {
      setLoading(false);
      return;
    }

    const params: CancelOrderParams = {
      id: orderRow.orderId,
      segid: orderRow.segmentId,
      recaptchaToken: token,
      depId: orderRow.departmentId,
      branchid: orderRow.branchId,
      usercode: orderRow.userCode,
    };

    const response = await cancelOrder(params);
    if (response && !response?.hasErrors) {
      showSuccess(t('mo.order_cancelled', { status: response?.myOrder?.orderRow?.status }));
      onSuccess(response.myOrder);
    } else if (response?.hasErrors) {
      showError(response?.errorMessage);
    }

    setIsLoading(false);
  };

  useOrdersAction(
    (action) =>
      action.action === 'cancel' &&
      actionTargetsOrder(action, { segmentId: orderRow.segmentId, orderId: orderRow.orderId }, isActive),
    (action) => {
      if (action.confirm) {
        void onClickCancel();
        return;
      }
      setOpenModal(true);
    },
    isActive,
  );

  return (
    <Fragment>
      <Button
        onClick={() => setOpenModal(true)}
        title={t(isLoading ? 'mo.loading' : 'mo.cancel_order')}
        className="btn btn-danger btn-ssm"
        disabled={isLoading}
        icon={trashIcon ? faTrash : faCircleXmark}
        iconclassname="mr-5"
      />

      {openModal &&
        createPortal(
          <div
            className="modal-overlay modal-overlay-top modal-maximizable"
            style={{ direction: 'ltr' }}
            ref={modalOverlayRef}
            onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
          >
            <div ref={modalRef} className={classList} style={modalStyle}>
              <div
                className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
                {...dragHandleProps}
              >
                <h5 className="title">
                  {t('mo.cancel_order')}
                  {' '}-{' '}Order: {orderRow.orderId}{' '}
                  Segment: {orderRow.segmentId}{' '}
                  {!!orderRow.status && (
                    <span className={`mo-status ${getStatus(orderRow.status)}`}>
                      {orderRow.status}
                    </span>
                  )}
                </h5>
                <div data-no-drag="true">
                  <ScreenshotSupport />
                  <MaximizeModal modalOverlayRef={modalOverlayRef} />
                  <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
                </div>
              </div>
              {step === 0 && (
                <div className="modal-content">
                  <div className="mb-10">{t('mo.segments')}</div>
                  <div>
                    <Checkbox
                      checked={true}
                      htmlLabel={
                        <span>
                          <span className="text-bold mr-5">[{orderRow.segmentId}]</span>
                          <span>{orderRow.itemDesc}</span>
                        </span>
                      }
                    />
                  </div>
                </div>
              )}
              {step === 1 && (
                <div className="modal-content">
                  <div className="text-md text-bold mb-10">{t('mo.booking_about_to_cancel')}</div>

                  <div className="">{t('mo.are_you_sure')}</div>
                </div>
              )}

              <div className="modal-footer">
                {step === 0 && (
                  <Button
                    className="btn btn-danger ml-0 mr-a"
                    title={t('mo.cancel_order')}
                    onClick={() => setStep(1)}
                  />
                )}
                {step === 1 && (
                  <Button
                    className="btn btn-danger ml-0 mr-a"
                    title={t('confirm')}
                    disabled={loading}
                    onClick={() => onClickCancel()}
                  />
                )}
                <Button
                  className="btn btn-default"
                  title={t('close')}
                  onClick={() => setOpenModal(false)}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </Fragment>
  );
};

export default withErrorBoundary(MoCancelModal, 'MoCancelModal');
